import { createPopper } from '@popperjs/core/lib/popper-lite'
import { flip, offset, preventOverflow, arrow } from '@popperjs/core'
import { randomString, includes } from '../../utils'
import { defaultOptions, availablePositions, textAlignments } from './constants'
import type { Placement, Instance, Modifier } from '@popperjs/core'
import type { DirectiveBinding } from 'vue'

export type TargetElement = HTMLElement & {
  __tooltip?: Tooltip
}

export type TooltipOptions = {
  label: string
  offset: [number, number]
  placement: Placement
  show: boolean
  textAlign: string
  isFullLength: boolean
  zIndex: number
  withinTarget: boolean
}

export type ToolTipOptionsInput =
  | string
  | Partial<Omit<TooltipOptions, 'placement'> & { position: Placement }>

export type AvailablePositions = (typeof availablePositions)[number]
export type TextAlignments = (typeof textAlignments)[number]
type BoundingBox = { x: number; y: number; width: number; height: number }

export class Tooltip {
  // Static property to track the currently active tooltip
  private static activeTooltip: Tooltip | null = null
  // Static property to track if the user is currently tabbing
  private static isCurrentlyTabbing = false

  private tooltipEl: HTMLElement | null = null
  private triggerEl: TargetElement | null = null
  private popperInstance: Instance | null = null
  private bridgeEl: HTMLElement | null = null

  private triggerHover = false
  private tooltipHover = false
  private bridgeHover = false
  private clickActive = false // Track if click just happened
  private options: TooltipOptions = { ...defaultOptions }
  private scrollableParents: (HTMLElement | Window)[] = []
  private timer: ReturnType<typeof setTimeout> | undefined = undefined

  constructor(trigger: TargetElement, opts: ToolTipOptionsInput | undefined) {
    this.triggerEl = trigger
    this.setOptions(opts)
  }

  show(): void {
    const { label, show: showTooltip, withinTarget } = this.options

    if (
      !label ||
      !label.length ||
      !showTooltip ||
      !this.triggerEl ||
      this.tooltipEl ||
      this.clickActive
    ) {
      return
    }

    // Close any existing active tooltip first
    if (Tooltip.activeTooltip && Tooltip.activeTooltip !== this) {
      Tooltip.activeTooltip.hide()
    }

    // Set this as the active tooltip
    Tooltip.activeTooltip = this

    // Create tooltip
    const tooltip = this.createTooltip()
    const tooltipId = tooltip.id || `sb-tooltip-${randomString(5)}`
    tooltip.id = tooltipId
    this.triggerEl.setAttribute('aria-describedby', tooltipId)
    this.triggerEl.setAttribute('aria-expanded', 'true')

    this.tooltipEl = tooltip
    this.addTooltipListeners()

    if (withinTarget) {
      this.triggerEl.appendChild(this.tooltipEl)
    } else {
      document.body.appendChild(this.tooltipEl)
    }

    this.addScrollListeners()
    this.addKeydownListener()
    this.setPopperInstance()

    // Create the bridge element immediately after tooltip is positioned
    requestAnimationFrame(() => {
      this.createBridge()
    })
  }

  hide(): void {
    if (!this.popperInstance && !this.tooltipEl) {
      return
    }

    // Update ARIA state
    if (this.triggerEl) {
      this.triggerEl.setAttribute('aria-expanded', 'false')
    }

    // Clear this as the active tooltip if it's currently active
    if (Tooltip.activeTooltip === this) {
      Tooltip.activeTooltip = null
    }

    this.cleanupTooltipInstances()
  }

  addTriggerListeners(): void {
    if (!this.triggerEl) {
      return
    }
    this.triggerEl.addEventListener('pointerenter', this.onTriggerEnter, {
      passive: true,
    })
    this.triggerEl.addEventListener('pointerleave', this.onTriggerLeave, {
      passive: true,
    })
    this.triggerEl.addEventListener('pointerdown', this.onTriggerPointerDown, {
      passive: true,
    })
    this.triggerEl.addEventListener('click', this.onTriggerClick, {
      passive: true,
    })
    this.triggerEl.addEventListener('focus', this.onTriggerFocus)
    this.triggerEl.addEventListener('blur', this.onTriggerBlur)
  }

  removeTriggerListeners(): void {
    if (!this.triggerEl) {
      return
    }
    this.triggerHover = false
    this.clickActive = false

    // Clear this as active tooltip when removing listeners
    if (Tooltip.activeTooltip === this) {
      Tooltip.activeTooltip = null
    }

    this.triggerEl.removeEventListener('pointerenter', this.onTriggerEnter)
    this.triggerEl.removeEventListener('pointerleave', this.onTriggerLeave)
    this.triggerEl.removeEventListener('pointerdown', this.onTriggerPointerDown)
    this.triggerEl.removeEventListener('click', this.onTriggerClick)
    this.triggerEl.removeEventListener('focus', this.onTriggerFocus)
    this.triggerEl.removeEventListener('blur', this.onTriggerBlur)
    this.removeScrollListeners()
    this.removeKeydownListener()
    this.cleanupTooltipInstances()
  }

  setOptions(opts: ToolTipOptionsInput | undefined): void {
    const merged: TooltipOptions =
      typeof opts === 'string'
        ? { ...this.options, label: opts }
        : {
            label: opts?.label ?? this.options.label,
            offset: opts?.offset ?? this.options.offset,
            placement: opts?.position ?? this.options.placement,
            show: opts?.show ?? this.options.show,
            textAlign: opts?.textAlign ?? this.options.textAlign,
            isFullLength: opts?.isFullLength ?? this.options.isFullLength,
            zIndex: opts?.zIndex ?? this.options.zIndex,
            withinTarget: opts?.withinTarget ?? this.options.withinTarget,
          }

    this.options = merged

    if (this.tooltipEl && merged.label) this.setTooltipContent(merged.label)
    if (this.popperInstance) this.popperInstance.update()

    // Update bridge if it exists
    if (this.bridgeEl) {
      requestAnimationFrame(() => {
        this.removeBridge()
        this.createBridge()
      })
    }
  }

  handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Tab') {
      Tooltip.isCurrentlyTabbing = true
    }

    if (event.key === 'Escape') {
      // Hide the currently active tooltip (which should be this one)
      if (Tooltip.activeTooltip) {
        Tooltip.activeTooltip.hide()
      }
    }
  }

  private setPopperInstance(): void {
    if (!this.triggerEl || !this.tooltipEl) {
      return
    }
    const { placement, withinTarget, offset: offsetOption } = this.options
    const centerArrow = this.getCenteredArrowModifier()
    this.popperInstance = createPopper(this.triggerEl, this.tooltipEl, {
      placement: placement,
      strategy: withinTarget ? 'fixed' : 'absolute',
      modifiers: [
        flip,
        preventOverflow,
        offset,
        {
          name: 'offset',
          options: {
            offset: offsetOption,
          },
        },
        arrow,
        centerArrow,
      ],
    })
  }

  private getCenteredArrowModifier(): Modifier<
    'centerArrow',
    Record<string, unknown>
  > {
    return {
      name: 'centerArrow',
      enabled: true,
      phase: 'write',
      requires: ['arrow'], // ensure it runs after Popper's arrow
      fn({ state }): void {
        const arrowEl = state.elements.arrow as HTMLElement | undefined
        if (!arrowEl) return

        // Reset any arrow offsets Popper computed (optional, defensive)
        state.modifiersData.arrow = { x: 0, y: 0, centerOffset: 0 }
      },
    }
  }

  private scheduleHide(): void {
    this.clearTimer()

    if (
      this.triggerHover ||
      this.tooltipHover ||
      this.bridgeHover ||
      document.activeElement === this.triggerEl
    ) {
      return
    }

    if (!this.triggerEl || !this.tooltipEl) {
      this.hide()
      return
    }

    this.timer = setTimeout(() => {
      // Double-check hover states before hiding
      if (
        this.triggerHover ||
        this.tooltipHover ||
        this.bridgeHover ||
        document.activeElement === this.triggerEl
      ) {
        return
      }

      this.hide()
    })
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = undefined
    }
  }

  private createTooltip(): HTMLElement {
    const el = document.createElement('div')
    el.setAttribute('role', 'tooltip')
    el.className = `sb-tooltip sb-tooltip--text--${this.options.textAlign}`
    el.style.display = 'unset'
    if (this.options.zIndex != null)
      el.style.zIndex = String(this.options.zIndex)
    el.style.maxWidth = this.options.isFullLength ? '100%' : '180px'
    this.setTooltipContent(this.options.label ?? '', el)

    const arrowEl = document.createElement('div')
    arrowEl.setAttribute('data-popper-arrow', 'true')
    arrowEl.setAttribute('role', 'presentation')
    el.appendChild(arrowEl)
    return el
  }

  private setTooltipContent(text: string, el?: HTMLElement): void {
    const target = el ?? this.tooltipEl
    if (target) target.textContent = text
  }

  private addTooltipListeners(): void {
    if (!this.tooltipEl) {
      return
    }
    this.tooltipEl.addEventListener('pointerenter', this.onTooltipEnter, {
      passive: true,
    })
    this.tooltipEl.addEventListener('pointerleave', this.onTooltipLeave, {
      passive: true,
    })
  }

  private removeTooltipListeners(): void {
    if (!this.tooltipEl) {
      return
    }
    this.tooltipEl.removeEventListener('pointerenter', this.onTooltipEnter)
    this.tooltipEl.removeEventListener('pointerleave', this.onTooltipLeave)
  }

  private setScrollableParents(): void {
    this.scrollableParents = []
    let parent: HTMLElement | null = this.triggerEl?.parentElement ?? null

    while (parent) {
      const style = window.getComputedStyle(parent)
      const overflowY = style.overflowY
      const overflowX = style.overflowX
      const yScrollable =
        (overflowY === 'auto' || overflowY === 'scroll') &&
        parent.scrollHeight > parent.clientHeight
      const xScrollable =
        (overflowX === 'auto' || overflowX === 'scroll') &&
        parent.scrollWidth > parent.clientWidth
      if (yScrollable || xScrollable) this.scrollableParents.push(parent)
      parent = parent.parentElement
    }
  }

  private addScrollListeners(): void {
    this.setScrollableParents()
    this.scrollableParents.forEach((sp) => {
      sp.addEventListener('scroll', this.onScroll, { passive: true })
    })
    document.addEventListener('scroll', this.onScroll, { passive: true })
  }

  private removeScrollListeners(): void {
    this.scrollableParents.forEach((sp) => {
      sp.removeEventListener('scroll', this.onScroll)
    })
    this.scrollableParents = []
    document.removeEventListener('scroll', this.onScroll)
  }

  private addKeydownListener(): void {
    document.addEventListener('keydown', this.handleKeydown)
  }

  private removeKeydownListener(): void {
    document.removeEventListener('keydown', this.handleKeydown)
  }

  private cleanupTooltipInstances(): void {
    this.clearTimer()
    this.removeBridge()
    this.removeTooltipListeners()

    if (this.tooltipEl) {
      this.tooltipEl.remove()
      this.tooltipEl = null
    }

    if (this.triggerEl) {
      this.triggerEl.removeAttribute('aria-describedby')
      this.triggerEl.removeAttribute('aria-expanded')
    }

    if (this.popperInstance) {
      this.popperInstance.destroy()
      this.popperInstance = null
    }
  }

  private createBridge(): void {
    if (!this.triggerEl || !this.tooltipEl || this.bridgeEl) {
      return
    }

    const triggerRect = this.triggerEl.getBoundingClientRect()
    const tooltipRect = this.tooltipEl.getBoundingClientRect()

    // Create invisible bridge element
    this.bridgeEl = document.createElement('div')

    // Use placement from Popper if available, otherwise use the default placement.
    const placement =
      this.popperInstance?.state.placement ?? this.options.placement
    const bridgeGeometry = this.calculateBridgeGeometry(
      {
        x: triggerRect.x,
        y: triggerRect.y,
        width: triggerRect.width,
        height: triggerRect.height,
      },
      {
        x: tooltipRect.x,
        y: tooltipRect.y,
        width: tooltipRect.width,
        height: tooltipRect.height,
      },
      placement,
    )

    // Skip bridge creation if geometry is invalid
    if (bridgeGeometry.width === 0 || bridgeGeometry.height === 0) {
      this.bridgeEl.remove()
      this.bridgeEl = null
      return
    }

    // Position bridge based on withinTarget option to handle transforms correctly
    if (this.options.withinTarget) {
      // When within target, convert viewport coordinates to local coordinates
      // This handles transforms by offsetting against the target's position
      this.bridgeEl.style.cssText = `
        position: absolute;
        background: transparent;
        pointer-events: auto;
        z-index: ${this.options.zIndex};
        left: ${bridgeGeometry.x - triggerRect.x}px;
        top: ${bridgeGeometry.y - triggerRect.y}px;
        width: ${bridgeGeometry.width}px;
        height: ${bridgeGeometry.height}px;
      `
      this.triggerEl.appendChild(this.bridgeEl)
    } else {
      // When not within target, use fixed positioning with viewport coordinates
      this.bridgeEl.style.cssText = `
        position: fixed;
        background: transparent;
        pointer-events: auto;
        z-index: ${this.options.zIndex};
        left: ${bridgeGeometry.x}px;
        top: ${bridgeGeometry.y}px;
        width: ${bridgeGeometry.width}px;
        height: ${bridgeGeometry.height}px;
      `
      document.body.appendChild(this.bridgeEl)
    }

    // Add hover listeners to the bridge
    this.bridgeEl.addEventListener('pointerenter', this.onBridgeEnter, {
      passive: true,
    })
    this.bridgeEl.addEventListener('pointerleave', this.onBridgeLeave, {
      passive: true,
    })
  }

  private calculateBridgeGeometry(
    ref: BoundingBox,
    pop: BoundingBox,
    placement: string,
  ): BoundingBox {
    const empty = { x: 0, y: 0, width: 0, height: 0 }

    if (
      (pop.x === 0 && pop.y === 0) ||
      !ref.width ||
      !ref.height ||
      !pop.width ||
      !pop.height
    ) {
      return empty
    }

    const [minX, minY] = [Math.min(ref.x, pop.x), Math.min(ref.y, pop.y)]
    const [maxX, maxY] = [
      Math.max(ref.x + ref.width, pop.x + pop.width),
      Math.max(ref.y + ref.height, pop.y + pop.height),
    ]

    const bridges = {
      top: (): BoundingBox => ({
        x: minX,
        y: pop.y + pop.height,
        width: maxX - minX,
        height: ref.y - (pop.y + pop.height),
      }),
      bottom: (): BoundingBox => ({
        x: minX,
        y: ref.y + ref.height,
        width: maxX - minX,
        height: pop.y - (ref.y + ref.height),
      }),
      left: (): BoundingBox => ({
        x: pop.x + pop.width,
        y: minY,
        width: ref.x - (pop.x + pop.width),
        height: maxY - minY,
      }),
      right: (): BoundingBox => ({
        x: ref.x + ref.width,
        y: minY,
        width: pop.x - (ref.x + ref.width),
        height: maxY - minY,
      }),
    }

    const key = placement.split('-')[0] as keyof typeof bridges
    const bridge = bridges[key]?.() || empty

    return bridge
  }

  private removeBridge(): void {
    if (!this.bridgeEl) {
      return
    }

    this.bridgeEl.removeEventListener('pointerleave', this.onBridgeLeave)
    this.bridgeEl.remove()
    this.bridgeEl = null
    this.bridgeHover = false
  }

  private onBridgeEnter = (): void => {
    this.bridgeHover = true
    this.clearTimer()
  }

  private onBridgeLeave = (): void => {
    this.bridgeHover = false
    this.scheduleHide()
  }

  private onTriggerEnter = (): void => {
    // Close any other active tooltip first
    if (Tooltip.activeTooltip && Tooltip.activeTooltip !== this) {
      Tooltip.activeTooltip.hide()
    }

    this.triggerHover = true
    this.clearTimer()

    // Only show if tooltip doesn't exist and not in click mode
    if (!this.tooltipEl && !this.clickActive) {
      this.show()
    }
  }

  private onTriggerLeave = (): void => {
    if (!this.tooltipEl) {
      return
    }

    this.triggerHover = false
    this.scheduleHide()
  }

  private onTriggerPointerDown = (): void => {
    // Close any active tooltip immediately on any click
    if (Tooltip.activeTooltip) {
      Tooltip.activeTooltip.hide()
    }

    // Set click active flag to prevent tooltip from showing
    this.clickActive = true

    // Reset the flag after a short delay to allow for normal hover behavior later
    // Use requestAnimationFrame + setTimeout to ensure it runs after click events
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.clickActive = false
      }, 150) // Small delay to prevent immediate re-showing
    })
  }

  private onTriggerClick = (): void => {
    // Ensure any active tooltip is hidden on click
    if (Tooltip.activeTooltip) {
      Tooltip.activeTooltip.hide()
    }
  }

  private onTriggerFocus = (): void => {
    if (!this.clickActive && Tooltip.isCurrentlyTabbing) {
      this.show()
    }
    Tooltip.isCurrentlyTabbing = false
  }

  private onTriggerBlur = (): void => this.scheduleHide()

  private onTooltipEnter = (): void => {
    this.tooltipHover = true
    this.clearTimer()
  }

  private onTooltipLeave = (): void => {
    this.tooltipHover = false
    this.scheduleHide()
  }

  private onScroll = (): void => {
    // Hide any active tooltip on scroll
    if (Tooltip.activeTooltip) {
      Tooltip.activeTooltip.hide()
    }
  }
}

/**
 * @function validateBinding
 * @param   {DirectiveBinding<string | ToolTipOptionsInput>} binding
 * @returns {Boolean}
 */
export const validateBinding = (
  binding: DirectiveBinding<string | ToolTipOptionsInput>,
): boolean => {
  if (
    typeof binding.value === 'object' &&
    typeof binding.value.position === 'string'
  ) {
    const value = includes(availablePositions, binding.value.position)

    if (!value) {
      console.warn(
        `[v-tooltip] position ${binding.value.position} is not valid`,
      )
    }

    return value
  }

  return true
}
